import type { Priority } from '@servicetim/shared';
import {
  useDeletePriority,
  usePriorities,
  useReorderPriorities,
  useUpdatePriority,
} from '@servicetim/ui/api';
import {
  Badge,
  Button,
  type ColumnDef,
  DataTable,
  toast,
  useConfirm,
} from '@servicetim/ui/components';
import { Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { moveItem } from '../lib/list-utils';
import { notifyError } from '../lib/notify';
import { ReorderButtons } from '../ui/reorder-buttons';
import { PriorityDialog } from './priority-dialog';

/** Priorities in rank order: reorder, set default, create/edit, delete (API rules surfaced). */
export function PrioritiesTable({ projectId }: { projectId: string }) {
  const priorities = usePriorities(projectId);
  const reorder = useReorderPriorities(projectId);
  const update = useUpdatePriority(projectId);
  const remove = useDeletePriority(projectId);
  const confirm = useConfirm();
  const [editing, setEditing] = useState<Priority | null | 'new'>(null);
  const rows = priorities.data ?? [];

  const move = (from: number, to: number) => {
    const ids = moveItem(rows, from, to).map((p) => p.id);
    reorder.mutate({ ids }, { onError: (e) => notifyError(e, 'Could not reorder') });
  };

  const setDefault = (p: Priority) =>
    update.mutate(
      { id: p.id, patch: { isDefault: true } },
      {
        onSuccess: () => toast.success(`${p.name} is now the default priority`),
        onError: (e) => notifyError(e),
      },
    );

  const onDelete = async (p: Priority) => {
    const ok = await confirm({
      title: `Delete priority "${p.name}"?`,
      description: 'SLA goals keyed by this priority are removed with it.',
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: () => remove.mutateAsync(p.id),
    });
    if (ok) toast.success(`Priority "${p.name}" deleted`);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: handlers and mutation resets are stable for the component lifetime
  const columns = useMemo<ColumnDef<Priority>[]>(
    () => [
      {
        id: 'order',
        header: 'Rank',
        size: 120,
        enableSorting: false,
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1">
            <span className="w-5 text-right tabular-nums">{row.original.rank}</span>
            <ReorderButtons
              label={row.original.name}
              disabled={reorder.isPending}
              onUp={row.index > 0 ? () => move(row.index, row.index - 1) : undefined}
              onDown={
                row.index < rows.length - 1 ? () => move(row.index, row.index + 1) : undefined
              }
            />
          </span>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-2 font-medium text-fg">
            <span
              aria-hidden="true"
              className="size-3 rounded-full border border-border-strong"
              style={{ backgroundColor: row.original.color ?? 'transparent' }}
            />
            {row.original.name}
          </span>
        ),
      },
      {
        accessorKey: 'urgency',
        header: 'Urgency',
        enableSorting: false,
        size: 110,
        cell: ({ getValue }) =>
          getValue<string>() === 'high' ? (
            <Badge variant="destructive">High</Badge>
          ) : (
            <Badge variant="secondary">Low</Badge>
          ),
      },
      {
        id: 'default',
        header: 'Default',
        enableSorting: false,
        size: 140,
        cell: ({ row }) =>
          row.original.isDefault ? (
            <Badge variant="info">
              <Star className="size-3" />
              Default
            </Badge>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDefault(row.original)}
              disabled={update.isPending}
            >
              Set default
            </Button>
          ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        size: 100,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Edit ${row.original.name}`}
              onClick={() => setEditing(row.original)}
            >
              <Pencil />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Delete ${row.original.name}`}
              title={
                row.original.isDefault
                  ? 'Make another priority the default first'
                  : rows.length <= 1
                    ? 'A project needs at least one priority'
                    : undefined
              }
              disabled={row.original.isDefault || rows.length <= 1}
              onClick={() => void onDelete(row.original)}
            >
              <Trash2 />
            </Button>
          </div>
        ),
      },
    ],
    [rows, reorder.isPending, update.isPending],
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={priorities.data}
        getRowId={(p) => p.id}
        isLoading={priorities.isPending}
        error={priorities.error}
        onRetry={() => void priorities.refetch()}
        enableSorting={false}
        caption="Priorities"
        emptyTitle="No priorities"
        toolbar={
          <div className="flex justify-end">
            <Button onClick={() => setEditing('new')}>
              <Plus />
              New priority
            </Button>
          </div>
        }
      />
      <PriorityDialog
        projectId={projectId}
        priority={editing === 'new' ? null : editing}
        nextRank={rows.length + 1}
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      />
    </>
  );
}
